import { useState } from 'react';
import { api } from '../../api/client';
import { B } from '../../constants/brand';
import { Btn, Modal } from '../ui';
import { NaicsField } from './NaicsField';

// Set a deal's NAICS code by hand, from wherever it is being read.
//
// Shared by the Deal NAICS Report and the deal report's industry column, so the
// two cannot drift — and so the RULE only has to be explained once, here: a code
// set this way is marked 'user' and is thereafter NEVER overwritten by the
// classifier. That is the point of setting it by hand, and it is also a
// commitment, so the modal says so rather than leaving it to be discovered.
//
// Saving reports the API's refusal verbatim. A code that is well-formed but not
// a published NAICS 2022 code comes back named, with the nearest real code
// offered — far more useful than "invalid", and the only reason the client does
// not try to second-guess it.
export const NaicsEditModal = ({ deal, onClose, onSaved }) => {
  const [code, setCode]         = useState(deal?.naicsCode || '');
  const [description, setDesc]  = useState(deal?.naicsDescription || '');
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState(null);

  const dirty = (code || '') !== (deal?.naicsCode || '');

  const save = async (nextCode) => {
    setSaving(true); setErr(null);
    try {
      // An empty code CLEARS it, which hands the deal back to the classifier —
      // source and signature are both nulled server-side.
      const res = await api.put(`deals/${deal.dealID}/naics`, { code: nextCode ?? code });
      onSaved?.({
        dealID: deal.dealID,
        naicsCode: res?.code || null,
        naicsDescription: res?.description || null,
        naicsSource: res?.source || null,
      });
      onClose();
    } catch (e) {
      setErr(e.message || 'Could not save the code.');
      setSaving(false);
    }
  };

  return (
    <Modal title={`Industry — ${deal?.name || 'deal'}`} onClose={saving ? () => {} : onClose} width={520}>
      <NaicsField
        value={code}
        description={description}
        onChange={(c, title) => { setCode(c); setDesc(title || ''); setErr(null); }}
        hint="Search an industry or a code. Leave it blank to let the classifier derive one from the deal’s documents."
      />

      <div style={{ fontSize: 12, color: B.textLt, marginTop: -6, marginBottom: 14 }}>
        A code you set here is kept as yours — the automatic classifier will not change it.
      </div>

      {err && (
        <div style={{ fontSize: 13, color: B.red, marginBottom: 12 }}>{err}</div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Only offered when there is something to remove. Clearing is not a
            destructive act here — it hands the deal back to the sweep. */}
        {deal?.naicsCode
          ? <Btn variant="ghost" disabled={saving} onClick={() => save('')}>Remove code</Btn>
          : <span />}
        <div style={{ display: 'flex', gap: 10 }}>
          <Btn variant="ghost" disabled={saving} onClick={onClose}>Cancel</Btn>
          <Btn disabled={saving || !dirty} onClick={() => save()}>
            {saving ? 'Saving…' : 'Save'}
          </Btn>
        </div>
      </div>
    </Modal>
  );
};
